<template>
    <div class="comments" :class="{ 'is-skeleton': !isInitialized }">

        <div class="submit-section" v-if="user.comments_banned == false">

            <h6 class="is-6 subtitle has-text-centered is-uppercase">
                <span>Добавить сообщение</span>
            </h6>

            <div class="columns">
                <div class="column">
                    <div class="field textarea-field">
                        <textarea-autosize
                            :disabled="isSubmitting"
                            rows="1"
                            class="textarea"
                            placeholder="Текст сообщения (Ctrl+Enter - отправить)"
                            :has-counter="maxLength"
                            :maxlength="maxLength"
                            v-model.trim="content"
                            @keyup.native.ctrl.enter="submit"
                        />
                        <div class="textarea-buttons">
                            <button
                                class="button has-badge-rounded is-badge-primary"
                                :data-badge="attachmentsCount"
                                :class="{
                                    'is-toggled': showAttachments,
                                    'badge': !showAttachments && attachmentsCount > 0,
                                }"
                                @click="toggleAttachments">
                                <b-icon icon="paperclip"/>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <attachments
                v-show="showAttachments"
                @change="onAttachmentsChange"
                ref="attachments"
                :disabled="isSubmitting"
                :model="attachmentsModel"
                :show-author="false"
                :show-header="false"
                upload-position="is-top"
                can-upload
            />

            <div class="columns">
                <div class="submit-button column">
                    <div class="submit-button field has-text-centered">
                        <button
                            :disabled="!canSubmit"
                            class="submit button is-primary"
                            @click="submit">
                            <span>Отправить</span>
                            <i class="icon mdi mdi-send"></i>
                        </button>
                    </div>
                </div>
            </div>

        </div>

        <template v-if="comments.length > 0">

            <transition-group
                name="comments"
                tag="div">

                <comment
                    v-for="(comment, index) in comments"
                    :key="comment.id"
                    :value="comment"
                    :author="user"
                    :class="{
                        'is-new': getNew(comment),
                    }"
                    :is-multiple="getMultiple(index)"
                    :is-grouped="getGrouped(index)"
                    @remove="onCommentRemove"
                    @banned="updateBan"
                    @unbanned="updateBan"
                />

            </transition-group>

            <br>

            <p class="has-text-centered" v-if="!isLastPage">
                <button
                    @click="load"
                    :disabled="isLoading"
                    :class="{
                        'is-loading': isLoading,
                    }"
                    class="button is-rounded is-primary is-outlined">
                    <span>Загрузить ещё</span>
                </button>
            </p>

            <br>

        </template>

    </div>
</template>

<script>
import Comment from "./comments/Comment";
import Attachments from "./Attachments";

import toasts from "./mixins/toasts";
import HasUser from "../mixins/HasUser";

export default Vue.extend({
  components: {
    Comment,
    Attachments
  },
  props: {
    maxLength: {
      type: Number,
      default: -1
    },
    model: {
      type: Object,
      required: true
    }
  },
  mixins: [toasts, HasUser],
  data() {
    return {
      isInitialized: false,
      isSubmitting: false,
      isFetchingNew: false,
      isLoading: false,
      isLastPage: false,
      showAttachments: false,
      attachmentsCount: 0,
      currentPage: 1,
      perPage: 10,
      fetchNewIntervalPointer: null,
      fetchTimestamp: 0,
      fetchUpdateInterval: 5000,
      loaded: 0,
      content: "",
      files: [],
      prefetched: null,
      comments: []
    };
  },
  computed: {
    attachmentsModel() {
      if (this.prefetched && this.isInitialized) {
        return {
          id: this.prefetched.id,
          type: "comment"
        };
      } else {
        return null;
      }
    },
    canSubmit() {
      return (
        !this.isSubmitting &&
        (this.content.length > 0 || this.attachmentsCount > 0)
      );
    }
  },
  created() {
    axios
      .get(`/api/comments/${this.model.name}/${this.model.id}/prefetch`)
      .then(({ data: comment }) => {
        this.prefetched = comment;
        this.load();
      });
  },
  methods: {
    toggleAttachments() {
      this.showAttachments = !this.showAttachments;
    },
    onAttachmentsChange(attachments) {
      this.attachmentsCount = attachments.length;
      this.showAttachments = this.attachmentsCount > 0;
    },
    onCommentRemove(comment) {
      let index = _.findIndex(this.comments, { id: comment.id });

      if (index > -1) {
        this.comments.splice(index, 1);
      }
    },
    fetchNew() {
      if (this.isFetchingNew) {
        return;
      }

      clearTimeout(this.fetchNewIntervalPointer);

      this.isFetchingNew = true;

      axios
        .get(`/api/comments/${this.model.name}/${this.model.id}/new`, {
          params: {
            since: this.fetchTimestamp
          }
        })
        .then(({ data }) => {

          if (data.length > 0) {
            for (let comment of data) {
              this.comments.unshift(comment);
            }
          }

          this.updateTimestamp();
        })
        .finally(() => {
          this.isFetchingNew = false;
          this.fetchNewIntervalPointer = setTimeout(
            this.fetchNew,
            this.fetchUpdateInterval
          );
        });
    },
    updateTimestamp() {
      this.fetchTimestamp = Math.max(
        this.fetchTimestamp,
        this.$moment(_.get(_.first(this.comments), "posted_at")).unix()
      );
    },
    submit(e) {
      if (!this.canSubmit) {
        return;
      }

      this.isSubmitting = true;

      axios
        .post(`/api/comments/${this.prefetched.id}`, {
          content: this.content
        })
        .then(({ data }) => {
          this.fetchNew();

          axios
            .get(`/api/comments/${this.model.name}/${this.model.id}/prefetch`)
            .then(({ data: comment }) => {
              this.prefetched = comment;
              this.content = "";
            })
            .finally(() => {
              this.isSubmitting = false;
            });
        })
        .catch(() => {
          this.isSubmitting = false;
        });
    },
    load() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      return axios
        .get(`/api/comments/${this.model.name}/${this.model.id}`, {
          params: {
            limit: this.perPage,
            page: this.currentPage++
          }
        })
        .then(({ data: response }) => {
          let comments = response.data;

          comments.forEach(comment => {
            this.comments.push(comment);
          });

          this.updateTimestamp();

          this.fetchNew();

          this.loaded += response.loaded;

          if (this.loaded >= response.total) {
            this.isLastPage = true;
          }

          this.isInitialized = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    changeAttachments(e) {
      let {
        target: { files }
      } = e;

      for (let file of files) {
        this.files.push(file);
      }
    },
    updateBan(user) {
      for (let i = 0; i < this.comments.length; i++) {
        this.comments[i].user.comments_banned = user.comments_banned;
      }
    },
    getMultiple(index) {
      if (index >= this.comments.length - 1) {
        return false;
      } else {
        return (
          _.get(this.comments[index], "user.id") ==
          _.get(this.comments[index + 1], "user.id")
        );
      }
    },
    getGrouped(index) {
      if (index <= 0) {
        return false;
      } else {
        return (
          _.get(this.comments[index - 1], "user.id") ==
          _.get(this.comments[index], "user.id")
        );
      }
    },
    getNew(comment) {
      if (this.comments.length >= 2) {
        return this.$moment(comment.posted_at).isAfter(
          this.$moment(this.comments[1].posted_at)
        );
      } else {
        return false;
      }
    }
  }
});
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/Comments.vue